import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { userService } from '../services/userService';
import { benefitsService } from '@/services/benefitsService';
import { useToken } from './TokenContext';

const UserContext = createContext({});

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { idToken, tokenVersion } = useToken();

  // Atualizar usuário quando o token mudar
  useEffect(() => {
    const updateUserFromToken = async () => {
      if (idToken && !user) {
        try {
          await fetchUser(idToken);
        } catch (error) {
          console.error('Erro ao buscar usuário após mudança de token:', error);
        }
      } else if (!idToken && user) {
        // Token foi removido, limpar usuário
        clearUser();
      }
    };

    updateUserFromToken();
  }, [idToken, tokenVersion]);

  const fetchUser = useCallback(async (token) => {
    try {
      setLoading(true);
      setError(null);
      const userData = await userService.getUserInfo(token);
      setUser(userData);
      return userData;
    } catch (err) {
      setError('Failed to fetch user data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback(async (token, userData) => {
    try {
      setLoading(true);
      setError(null);
      const updatedUser = await userService.updateUser(token, userData);
      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      setError('Failed to update user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const registerUser = useCallback(async (registerData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.registerUser(registerData);
      return response;
    } catch (err) {
      setError('Failed to register user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearUser = useCallback(() => {
    setUser(null);
    setError(null);
    userService.logout();
    benefitsService.clearCache();
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        error,
        fetchUser,
        updateUser,
        registerUser,
        clearUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}; 
